import { Box, Divider, Skeleton, Tooltip, Typography } from "@mui/material";

import { useGetClientCryptors } from "@/api/stats.api";

interface ClientCryptorGalleryProps {
  collapsed: boolean;
}

const GalleryLoading = () => (
  <Box sx={{ px: 2, pt: 2 }}>
    <Skeleton width={90} height={18} />
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1,
        mt: 1,
      }}
    >
      <Skeleton
        variant="circular"
        sx={{ width: "100%", height: "auto", aspectRatio: "1 / 1" }}
      />
      <Skeleton
        variant="circular"
        sx={{ width: "100%", height: "auto", aspectRatio: "1 / 1" }}
      />
    </Box>
  </Box>
);

export const ClientCryptorGallery = ({
  collapsed,
}: ClientCryptorGalleryProps) => {
  const {
    data: collections,
    isLoading,
    isError,
  } = useGetClientCryptors(!collapsed);

  if (collapsed) return null;

  if (isLoading) {
    return (
      <Box component="li" sx={{ listStyle: "none" }}>
        <Divider sx={{ mt: 1 }} />
        <GalleryLoading />
      </Box>
    );
  }

  if (isError || !collections?.length) return null;

  return (
    <Box component="li" sx={{ listStyle: "none", pb: 2 }}>
      <Divider sx={{ my: 1.5 }} />
      <Typography
        component="h2"
        sx={{
          px: 2,
          mb: 1.5,
          color: "text.secondary",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        Mes œuvres
      </Typography>

      {collections.map((collection) => (
        <Box key={collection.id} sx={{ px: 2, mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              component="h3"
              sx={{
                minWidth: 0,
                color: "text.primary",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              {collection.name}
            </Typography>
            <Typography
              sx={{
                flexShrink: 0,
                color: "text.disabled",
                fontSize: 11,
              }}
            >
              {collection.cryptors.length}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {collection.cryptors.map((cryptor) => (
              <Tooltip
                key={cryptor.id}
                title={cryptor.name}
                placement="right"
                arrow
              >
                <Box
                  component="img"
                  src={cryptor.imageUrl}
                  alt={cryptor.name}
                  loading="lazy"
                  sx={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: "50%",
                    objectFit: "contain",
                    cursor: "default",
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
